/**
 * Work Atlas API - GET {flowBase}/atlas (docs/record-flow/api-contract.md,
 * "Atlas (Tide phase 1)"). The base comes from flowApi's getFlowBase(), so the
 * admin console reads /admin/record-flow/atlas and the manager portal reads
 * /record-flow/atlas behind its own gate.
 *
 * Resolves (never throws) to { ok: true, data } or { ok: false, error, status }.
 * status undefined = the server was not reached; that is never an empty atlas.
 */
import { apiRequest } from '../../../../services/api';
import { describeFailure, getFlowBase, type FlowResult } from '../flowApi';

export type AtlasDomain = 'PA' | 'UMED' | 'MANIFEST';
export const ATLAS_DOMAINS: AtlasDomain[] = ['PA', 'UMED', 'MANIFEST'];

export type DomainCounts = Record<AtlasDomain, number>;

export interface AtlasFacility {
  id: string;
  name: string;
  region: string | null;
  lat: number;
  lng: number;
  open: DomainCounts;
  total: number;
  /** Longest time in the current stage among open records whose stage-entry time is recorded; null = none recorded. */
  oldestOpenDays: number | null;
  /** Open records here with no recorded stage-entry time. Additive; absent from older servers. */
  unaged?: number;
  /** Visit plans dated today (America/New_York) that are PLANNED, CONFIRMED or COMPLETED. Not open work: a facility can have total 0 and visitsToday > 0. Absent from older servers. */
  visitsToday?: number;
}

export type NotLocatedReason = 'NO_FACILITY' | 'UNKNOWN_FACILITY' | 'MERGED' | 'NO_COORDINATES' | 'OUT_OF_BOUNDS';

export interface Atlas {
  generatedAt: string;
  facilities: AtlasFacility[];
  notLocated: { facilities: number; open: DomainCounts; reasons?: Partial<Record<NotLocatedReason, number>>; visitsToday?: number };
  totals: DomainCounts & { visitsToday?: number };
}

export async function fetchAtlas(): Promise<FlowResult<Atlas>> {
  const base = getFlowBase();
  const r = await apiRequest<Atlas>('GET', `${base}/atlas`);
  if (r.success && r.data) return { ok: true, data: r.data };
  if (r.success) return { ok: false, error: 'The server answered without any data, so nothing below is current.', status: r.status };
  return { ok: false, error: describeFailure(r, base), status: r.status, code: r.code };
}
